import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Collapse,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import MarkdownRenderer from "./MarkdownRenderer";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

/**
 * ThinkingRenderer component for displaying model reasoning in <think> tags
 * It renders the thinking content in a different color and makes it collapsible
 * It auto-expands during streaming and collapses when streaming ends
 * @param {boolean} compact - Whether to use compact styling for mobile
 */
const ThinkingRenderer = ({
  content,
  isStreaming = false,
  compact = false,
}) => {
  const theme = useTheme();
  // Start expanded if we're streaming, otherwise collapsed
  const [expanded, setExpanded] = useState(isStreaming);
  // Keep track of previous streaming state to detect changes
  const [wasStreaming, setWasStreaming] = useState(isStreaming);

  // Auto-expand during streaming, auto-collapse when streaming ends
  useEffect(() => {
    // If streaming started, expand
    if (isStreaming) {
      setExpanded(true);
    }
    // If streaming just ended (was streaming but now isn't), collapse
    else if (wasStreaming && !isStreaming) {
      setExpanded(false);
    }

    // Update previous streaming state
    setWasStreaming(isStreaming);
  }, [isStreaming, wasStreaming]);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  if (!content) return null;

  return (
    <Box sx={{ mb: compact ? 1 : 1.5, mt: compact ? 0.3 : 0.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          borderBottom: "1px dashed",
          borderColor: "divider",
          py: compact ? 0.3 : 0.5,
          cursor: "pointer",
        }}
        onClick={toggleExpanded}
      >
        {isStreaming ? (
          <CircularProgress
            size={compact ? 12 : 14}
            sx={{
              marginInlineEnd: compact ? 0.5 : 1,
              color: theme.palette.text.secondary,
            }}
          />
        ) : (
          <AutoAwesomeIcon
            fontSize={compact ? "inherit" : "small"}
            sx={{
              marginInlineEnd: compact ? 0.5 : 1,
              color: theme.palette.text.secondary,
              fontSize: compact ? "0.8rem" : "0.9rem",
            }}
          />
        )}
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: compact ? "0.7rem" : "0.75rem",
            fontStyle: "italic",
            userSelect: "none",
          }}
        >
          {expanded ? "Hide reasoning" : "Show reasoning"}
        </Typography>
        <IconButton
          size="small"
          sx={{
            marginInlineStart: "auto",
            p: compact ? 0.2 : 0.25,
            color: theme.palette.text.secondary,
            "&:hover": {
              color: theme.palette.text.primary,
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            toggleExpanded();
          }}
        >
          {expanded ? (
            <ExpandLessIcon
              fontSize={compact ? "inherit" : "small"}
              sx={{ fontSize: compact ? "0.9rem" : undefined }}
            />
          ) : (
            <ExpandMoreIcon
              fontSize={compact ? "inherit" : "small"}
              sx={{ fontSize: compact ? "0.9rem" : undefined }}
            />
          )}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box
          sx={{
            pt: compact ? 0.5 : 1,
            pb: compact ? 0.3 : 0.5,
            paddingInlineStart: compact ? 1.5 : 2,
            borderInlineStart: "2px solid",
            borderColor:
              theme.palette.mode === "dark"
                ? theme.palette.grey[700]
                : theme.palette.grey[300],
            color:
              theme.palette.mode === "dark"
                ? theme.palette.info.light
                : theme.palette.info.dark,
            fontSize: compact ? "0.8em" : "0.9em",
          }}
        >
          <MarkdownRenderer content={content} compact={compact} />
        </Box>
      </Collapse>
    </Box>
  );
};

export default ThinkingRenderer;
